import { deriveHistoryCacheCap } from './history-cache.js';
import type { SDKStorage } from '../storage/types.js';
import { DurableCache } from './durable.js';

/**
 * List page-window cache — one page window per query signature + offset.
 * The same machinery the history cache uses (one LRU, identity-scoped,
 * recency-evicted, no clocks in freshness decisions); pages are stored
 * exactly as the resource returned them.
 *
 * Invalidation: `catalog.changed` for the affected scope, the
 * owning mutations at the mutation point, and reconnect (evict all — heal is
 * by refetch, never replay).
 */
export class PageWindowCache<P> {
  private entries = new Map<string, { page: P; lastReadAt: number }>();
  private lastIdentity: string | null = null;
  // Durable mirror: hydrate-once, write-through per entry.
  private durable: DurableCache<P>;

  constructor(
    private capEntries: number = deriveHistoryCacheCap(),
    private durableKeyPrefix = 'list:',
    storage: SDKStorage | null = null,
  ) {
    this.durable = new DurableCache<P>(
      storage,
      (key) => `la:cache:${this.durableKeyPrefix}${key}`,
      (namespacedKey, value) => {
        // Hydration re-inserts WITHOUT re-writing the mirror; strip the
        // owner's sub-prefix back to the cache key.
        const key = namespacedKey.replace(new RegExp(`^${this.durableKeyPrefix}`), '');
        this.entries.set(key, { page: value, lastReadAt: 0 });
      },
      () => [...this.entries.keys()],
      (key) => this.entries.get(key)?.page,
      (key) => this.entries.delete(key),
    );
  }

  /** An identity change evicts everything — entries never cross accounts. */
  prepare(identity: string): void {
    if (this.lastIdentity === null) {
      // First use: hydrate the durable snapshots (cold-start paint).
      this.durable.hydrate();
    }
    if (this.lastIdentity !== null && this.lastIdentity !== identity) {
      this.evictAll();
    }
    this.lastIdentity = identity;
  }

  /**
   * Synchronous peek at the LAST identity's entry — the TanStack
   * `initialData` seed: the return leg paints from the cache
   * on the first render, before any async identity resolution. Absent (or
   * after an identity switch, which evicted) → null.
   */
  peekLast(key: string): P | null {
    if (this.lastIdentity === null) return null;
    const entry = this.entries.get(key);
    return entry ? entry.page : null;
  }

  lookup(identity: string, key: string): P | null {
    this.prepare(identity);
    const entry = this.entries.get(key);
    if (!entry) return null;
    this.entries.delete(key);
    entry.lastReadAt = Date.now();
    this.entries.set(key, entry);
    return entry.page;
  }

  store(identity: string, key: string, page: P): void {
    this.prepare(identity);
    this.entries.delete(key);
    this.entries.set(key, { page, lastReadAt: Date.now() });
    this.durable.writeThrough(key, page);
    while (this.entries.size > this.capEntries) {
      const lru = this.entries.keys().next();
      if (lru.done) break;
      this.entries.delete(lru.value);
      this.durable.remove(lru.value);
    }
  }

  /** Evict every entry whose key starts with the prefix (a scope's windows). */
  evictPrefix(prefix: string): void {
    this.durable.evictDurablePrefix(prefix);
    for (const key of [...this.entries.keys()]) {
      if (key.startsWith(prefix)) this.entries.delete(key);
    }
  }

  evictAll(): void {
    this.entries.clear();
    this.durable.evictAllDurable();
  }
}
